import { LitElement, html, unsafeCSS } from "lit"
import { customElement, state, query } from "lit/decorators.js"
import { supportsFileSystemAccess, pickDirectory } from "../fs/directory"
import { scanFits } from "../fs/scanFits"
import { groupSloohFits } from "../fits/groupSloohFits"
import type { FitsFileMeta, FitsGroup } from "../fits/types"
import styles from "./styles/fits-viewer.css?inline"
import { analytics } from "../firebase"
import { logEvent } from "firebase/analytics"

type WorkerStage = "parsing" | "stretching" | "compositing"

@customElement("fits-viewer")
export class FitsViewer extends LitElement {
  @state() private isLoading = false
  @state() private errorMessage: string | null = null
  @state() private statusMessage: string | null = null

  @state() private directoryHandle: FileSystemDirectoryHandle | null = null
  @state() private fitsFiles: FitsFileMeta[] = []
  @state() private groups: FitsGroup[] = []
  @state() private selectedGroupId: string | null = null
  @state() private currentImageInfo: {
    width: number
    height: number
    baseKey: string | null
  } | null = null
  @state() private progressPercent = 0
  @state() private progressLabel: string | null = null

  @query("canvas") private canvasEl!: HTMLCanvasElement

  private fitsWorker: Worker | null = null
  private currentRequestId: string | null = null
  private pendingBaseKey: string | null = null

  static styles = unsafeCSS(styles)

  protected firstUpdated() {
    this.initializeWorker()
  }

  protected render() {
    return html`
      <div class="container">
        <section class="panel">
          <h1>FITS Viewer</h1>
          <p class="lead">
            Choose a local Slooh FITS folder, detect L/R/G/B sets, and view a
            composed color image directly in your browser.
          </p>
          <div class="toolbar">
            <button
              class="primary"
              ?disabled=${this.isLoading}
              @click=${this.handleChooseFolder}
            >
              ${this.isLoading ? "Loading…" : "Choose FITS folder"}
            </button>

            ${this.isLoading
              ? html`<div class="progress">
                  <div class="progress-bar">
                    <div
                      class="progress-bar-fill"
                      style="width: ${this.progressPercent}%"
                    ></div>
                  </div>
                  <span>
                    ${this.progressLabel ?? `${this.progressPercent}%`}
                  </span>
                </div>`
              : null}
          </div>

          ${this.groups.length
            ? html`
                <div class="group-row">
                  <label for="groupSelect">Target set</label>
                  <select
                    id="groupSelect"
                    @change=${this.handleGroupChange}
                    ?disabled=${this.isLoading}
                    .value=${this.selectedGroupId ?? ""}
                  >
                    ${this.groups.map(
                      group => html`
                        <option value=${group.id}>${group.baseKey}</option>
                      `
                    )}
                  </select>
                  <span class="group-meta">
                    ${this.selectedGroupId
                      ? `Showing RGB set for “${this.selectedGroupId}”`
                      : "Select a target set to render."}
                  </span>
                </div>

                <div class="controls-row">
                  <label>
                    Gamma
                    <input
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.1"
                      value="1"
                      @input=${this.handleGammaChange}
                    />
                  </label>
                  <button
                    class="export-button"
                    @click=${this.handleExportImage}
                    ?disabled=${!this.currentImageInfo}
                  >
                    Export PNG
                  </button>
                </div>
              `
            : null}
        </section>

        <div class="canvas-shell">
          <canvas></canvas>
        </div>

        ${this.currentImageInfo
          ? html`<div class="meta-row">
              Resolution: ${this.currentImageInfo.width} ×
              ${this.currentImageInfo.height}
              ${this.currentImageInfo.baseKey
                ? html` · Target: ${this.currentImageInfo.baseKey}`
                : null}
            </div>`
          : null}

        <div class="messages">
          ${this.errorMessage
            ? html`<div class="error">${this.errorMessage}</div>`
            : null}
          ${this.statusMessage
            ? html`<div class="status">${this.statusMessage}</div>`
            : null}
        </div>
      </div>
    `
  }

  private async handleGroupChange(event: Event) {
    const select = event.target as HTMLSelectElement
    const id = select.value || null
    this.selectedGroupId = id

    if (id) {
      this.statusMessage = `Selected target: ${id}`
      await this.renderSelectedGroup()
    }
  }

  private async handleChooseFolder() {
    this.errorMessage = null
    this.statusMessage = null
    this.progressPercent = 0
    this.progressLabel = null

    if (!supportsFileSystemAccess()) {
      this.errorMessage =
        "Your browser does not support the File System Access API. Please use a recent version of Chrome or Edge."
      return
    }

    this.isLoading = true
    this.setProgress(5, "Waiting for folder selection…")

    try {
      const directory = await pickDirectory()
      if (!directory) {
        // User cancelled.
        this.isLoading = false
        return
      }

      this.directoryHandle = directory
      const files = await scanFits(directory)
      this.setProgress(15, "Scanning folder for FITS files…")

      if (!files.length) {
        this.fitsFiles = []
        this.groups = []
        this.selectedGroupId = null
        this.errorMessage = "No FITS files found in this folder."
        this.isLoading = false
        return
      }

      this.fitsFiles = files

      const groups = groupSloohFits(files)
      this.setProgress(30, "Grouping Slooh L/R/G/B sets…")
      if (!groups.length) {
        this.groups = []
        this.selectedGroupId = null
        this.errorMessage =
          "Found FITS files, but no complete RGB sets (R, G, B) matching the expected Slooh naming pattern."
        this.statusMessage = `Discovered ${files.length} FITS file${
          files.length === 1 ? "" : "s"
        }.`
        this.isLoading = false
        return
      }

      this.groups = groups
      this.selectedGroupId = groups[0]?.id ?? null
      this.statusMessage = `Discovered ${groups.length} RGB set${
        groups.length === 1 ? "" : "s"
      } across ${files.length} FITS file${files.length === 1 ? "" : "s"}.`

      if (this.selectedGroupId) {
        this.setProgress(40, "Preparing to render RGB composite…")
        await this.renderSelectedGroup()

        // Track successful FITS viewer usage
        if (analytics) {
          logEvent(analytics, "open_fits_viewer", {
            files_count: files.length,
            groups_count: groups.length,
            selected_group: this.selectedGroupId,
          })
        }
      }
    } catch (err) {
      console.error(err)
      this.errorMessage =
        "An unexpected error occurred while reading the folder."
    } finally {
      this.isLoading = false
    }
  }

  private async renderSelectedGroup() {
    const group = this.groups.find(g => g.id === this.selectedGroupId)
    if (!group) return

    if (!group.files.r || !group.files.g || !group.files.b) {
      this.errorMessage =
        "Selected group is missing one or more of R, G, or B channels."
      return
    }

    // Prefer worker-based rendering when available to keep the UI responsive.
    if (this.fitsWorker) {
      await this.renderSelectedGroupWithWorker(group)
      return
    }

    // Fallback: if workers are unavailable, avoid heavy main-thread work.
    this.errorMessage =
      "Your browser does not support the FITS worker; rendering is disabled to avoid page freezes."
  }

  private setProgress(percent: number, label: string) {
    this.progressPercent = Math.max(0, Math.min(100, Math.round(percent)))
    this.progressLabel = label
  }

  private initializeWorker() {
    if (this.fitsWorker) return

    try {
      this.fitsWorker = new Worker(
        new URL("../fits/fits-worker.ts", import.meta.url),
        { type: "module" }
      )
      this.fitsWorker.onmessage = event => this.handleWorkerMessage(event)
    } catch (err) {
      console.error("Failed to initialize FITS worker", err)
      this.fitsWorker = null
    }
  }

  private async renderSelectedGroupWithWorker(group: FitsGroup) {
    const worker = this.fitsWorker
    if (!worker) return

    this.isLoading = true
    this.errorMessage = null
    this.setProgress(10, "Preparing render…")

    try {
      const channels: { band: "r" | "g" | "b" | "l"; buffer: ArrayBuffer }[] =
        []

      const loadChannel = async (
        band: "r" | "g" | "b" | "l",
        handle: FileSystemFileHandle
      ) => {
        const file = await handle.getFile()
        const buffer = await file.arrayBuffer()
        channels.push({ band, buffer })
      }

      await loadChannel("r", group.files.r!.handle)
      await loadChannel("g", group.files.g!.handle)
      await loadChannel("b", group.files.b!.handle)
      if (group.files.l) {
        await loadChannel("l", group.files.l.handle)
      }

      const id =
        (crypto as any).randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2)}`

      this.currentRequestId = id
      this.pendingBaseKey = group.baseKey

      worker.postMessage(
        {
          type: "render",
          id,
          baseKey: group.baseKey,
          channels,
        },
        channels.map(c => c.buffer)
      )
    } catch (err) {
      console.error(err)
      this.errorMessage = "Failed to load FITS data for worker."
      this.isLoading = false
    }
  }

  private handleWorkerMessage(event: MessageEvent) {
    const data = event.data as {
      type: "progress" | "result" | "error"
      id: string
      stage?: WorkerStage
      fraction?: number
      baseKey?: string
      width?: number
      height?: number
      rgba?: Uint8ClampedArray
      message?: string
    }

    if (!data || !data.id || data.id !== this.currentRequestId) {
      return
    }

    if (data.type === "progress" && data.stage) {
      const frac = data.fraction ?? 1
      switch (data.stage) {
        case "parsing":
          this.setProgress(40 * frac, "Parsing FITS headers and pixels…")
          break
        case "stretching":
          this.setProgress(70 * frac, "Stretching channels…")
          break
        case "compositing":
          this.setProgress(90 * frac, "Compositing image…")
          break
      }
      return
    }

    if (data.type === "error") {
      this.errorMessage =
        data.message ?? "An error occurred while rendering in the worker."
      this.isLoading = false
      return
    }

    if (data.type === "result" && data.width && data.height && data.rgba) {
      const canvas = this.canvasEl
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        this.errorMessage = "Unable to get 2D context from canvas."
        this.isLoading = false
        return
      }

      const width = data.width
      const height = data.height
      const imageData = new ImageData(data.rgba, width, height)

      canvas.width = width
      canvas.height = height
      ctx.putImageData(imageData, 0, 0)

      this.currentImageInfo = {
        width,
        height,
        baseKey: data.baseKey ?? this.pendingBaseKey,
      }
      this.statusMessage = `Rendered composite for ${
        this.currentImageInfo.baseKey ?? "target"
      }`
      this.setProgress(100, "Done")
      this.isLoading = false
    }
  }

  // v1 enhancement hooks. Current implementation re-renders with a new gamma
  // by simply triggering a fresh render; a more advanced version would keep
  // channel buffers around and only recompute stretches.
  private async handleGammaChange(_event: Event) {
    // For v1 simplicity, just trigger re-render of the current group.
    if (this.selectedGroupId) {
      await this.renderSelectedGroup()
    }
  }

  private async handleExportImage() {
    const canvas = this.canvasEl
    if (!canvas) return

    canvas.toBlob(blob => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      const base =
        this.currentImageInfo?.baseKey ??
        this.selectedGroupId ??
        "fits-composite"
      a.download = `${base}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, "image/png")
  }
}
